import MUIButton from '@mui/material/Button';
import { Link } from 'gatsby';
import { FC, MouseEvent, ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';

import { FlexProps, flex } from 'quarks/interpolations/flex';
import { allCSSWithPseudos } from 'quarks/styleProps/all';

import parseUrl from 'utils/parseUrl';

const StyledButton = styled(MUIButton).withConfig({
  shouldForwardProp: prop => !Object.keys(allCSSWithPseudos).includes(prop),
})`
  &&& {
    ${flex}
  }
`;

const StyledLink = styled(Link)`
  text-decoration: unset;
  color: inherit;

  @media (max-width: 575px) {
    width: 100%;

    &.text-link {
      width: fit-content;
    }
  }
  ${flex}
`;

export interface ButtonProps extends FlexProps {
  /**
   * Pass the label as a child. Works with a string or icon.
   */
  children: ReactNode;
  /**
   * ReactNode of icon to be put at the beginning of the button
   */
  endIcon?: ReactNode;
  className?: string;
  /**
   * Determines between an outlined, text, or contained button
   */
  variant?:
    | 'black'
    | 'gray-outlined'
    | 'primary'
    | 'secondary'
    | 'outlined'
    | 'black-outlined'
    | 'navy-outlined'
    | 'link'
    | 'white'
    | 'white-link';
  /**
   * The URL to link to when the button is clicked. If defined, an `a` element will be used as the root node.
   */
  href?: string;
  /**
   * If `true`, the button is disabled.
   */
  disabled?: boolean;
  /**
   * Callback fired when the button is clicked. Also fires on `space bar` and `enter` key presses when focused.
   * event: The event source of the callback. You can pull out the new value by accessing `event.target.value` (string).
   */
  onClick?: (event: MouseEvent) => void;
}

const Button: FC<ButtonProps> = ({ children, className, href, endIcon, ...props }) => {
  const [isFocused, setIsFocused] = useState(false);
  const urlProps = href ? parseUrl(href) : {};

  useEffect(() => {
    document.addEventListener('mouseup', () => setIsFocused(false));

    return () => document.removeEventListener('mouseup', () => setIsFocused(false));
  }, []);

  const commonButtonProps = {
    textDecoration: 'none',
    endIcon,
    border: '1px solid',
    borderColor: props?.variant === 'outlined' || props?.variant === 'navy-outlined' ? undefined : 'common-transparent',
    onMouseDown: () => setIsFocused(true),
    className: `${className}${isFocused ? ' focused' : ''}`,
    ...(!urlProps?.to ? urlProps : {}),
    ...props,
    width: '100%',
    maxWidth: '100%',
    sm: { maxWidth: 'fit-content' },
  };

  return urlProps?.to ? (
    <StyledLink
      to={urlProps.to}
      className={`${className}${props.variant === 'link' || props.variant === 'white-link' ? ' text-link' : ''}`}
    >
      <StyledButton {...commonButtonProps}>{children}</StyledButton>
    </StyledLink>
  ) : (
    <StyledButton {...commonButtonProps}>{children}</StyledButton>
  );
};

export default Button;

Button.defaultProps = {
  disabled: false,
  width: 'fit-content',
  justifyContent: 'center',
  alignItems: 'center',
};
